import { Box, List, MenuItem, styled, useBreakpointDown } from '@chronosphereio/chrono-ui';
import { areTimeRangeInputValuesEqual, TimeRangeInputValue } from '@chronosphereio/core';
import { forwardRef, useState } from 'react';
import { type TimeRangeSelectOption } from '../utils/options';

export interface RelativeTimeRangeListProps {
  /**
   * The current selected time range value
   */
  value: TimeRangeInputValue;
  /**
   * Callback when a time range option is selected
   */
  onChange: (newTimeRange: TimeRangeInputValue) => void;
  /**
   * Filtered options to display
   */
  filteredOptions: TimeRangeSelectOption[];
  /**
   * ID for the listbox element, shared with the search field for ARIA linkage
   */
  listboxId: string;
  /**
   * Called when Escape is pressed on the listbox (e.g. parent returns focus to the search field)
   */
  onEscape?: () => void;
}

export const RelativeTimeRangeList = forwardRef<HTMLUListElement, RelativeTimeRangeListProps>(
  function RelativeTimeRangeList({ value, onChange, filteredOptions, listboxId, onEscape }, ref) {
    const isMobile = useBreakpointDown('sm', { breakpointSource: 'viewport' });
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const selectOption = (option: TimeRangeSelectOption) => {
      if (option.value && typeof option.value !== 'string') {
        onChange(option.value);
      }
    };

    const isOptionSelected = (option: TimeRangeSelectOption) => {
      if (typeof option.value === 'string') return false;
      return areTimeRangeInputValuesEqual(option.value, value);
    };

    const scrollToOption = (index: number) => {
      document.getElementById(`${listboxId}-option-${index}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (filteredOptions.length === 0) return;
      const maxIndex = filteredOptions.length - 1;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = focusedIndex >= maxIndex ? 0 : focusedIndex + 1;
          setFocusedIndex(next);
          scrollToOption(next);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = focusedIndex <= 0 ? maxIndex : focusedIndex - 1;
          setFocusedIndex(prev);
          scrollToOption(prev);
          break;
        }
        case 'Escape': {
          e.preventDefault();
          e.stopPropagation();
          setFocusedIndex(-1);
          onEscape?.();
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex <= maxIndex) {
            selectOption(filteredOptions[focusedIndex]!);
          }
          break;
        }
        default:
          break;
      }
    };

    return (
      <RelativeTimeRangeListRoot>
        <OptionsList
          ref={ref}
          id={listboxId}
          tabIndex={-1}
          role="listbox"
          aria-activedescendant={
            focusedIndex >= 0 && focusedIndex < filteredOptions.length
              ? `${listboxId}-option-${focusedIndex}`
              : undefined
          }
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (focusedIndex < 0 && filteredOptions.length > 0) {
              setFocusedIndex(0);
              scrollToOption(0);
            }
          }}
          onBlur={() => setFocusedIndex(-1)}
        >
          {filteredOptions.map((option, index) => {
            const isSelected = isOptionSelected(option);

            return (
              <OptionItem
                key={option.key}
                id={`${listboxId}-option-${index}`}
                onClick={() => selectOption(option)}
                selected={isSelected}
                highlighted={index === focusedIndex}
                role="option"
                aria-selected={isSelected}
                isMobile={isMobile}
                tabIndex={-1}
              >
                {option.label}
              </OptionItem>
            );
          })}
        </OptionsList>
      </RelativeTimeRangeListRoot>
    );
  }
);

const RelativeTimeRangeListRoot = styled(Box)(() => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const OptionsList = styled(List)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  '&:focus': { outline: 'none' },
});

const OptionItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'isMobile',
})<{ highlighted?: boolean; isMobile?: boolean }>(({ theme, selected, highlighted, isMobile }) => ({
  padding: theme.spacing(1, 2),
  ...(!isMobile && { maxWidth: '100%' }),
  ...(selected && {
    backgroundColor: theme.palette.menu.item.selected,
  }),
  ...(highlighted &&
    !selected && {
      backgroundColor: theme.palette.menu.item.hover,
    }),
}));
